import * as amplitude from '@amplitude/analytics-browser';
import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import * as Sentry from '@sentry/angular-ivy';
import { BehaviorSubject, from, of, timer } from 'rxjs';
import { catchError, filter, map, switchMap, take, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthCompany, AuthUser, User } from 'src/models';
import { BillingDetailsApiData } from '../pages/new-billing/types/api.types';
import { AuthSelectors } from '../store/auth/auth.selectors';
import { ClearGuideProgress, SaveGuideProgress } from '../store/guide-progress/guide-progress.actions';
import { GuideProgressState } from '../store/guide-progress/guide-progress.state';
import { routePath } from '../util/painpoint-landing.util';
import {
  evaluateSessionReplayCapability,
  readSessionReplayDeviceSignals,
  SESSION_REPLAY_SAMPLE_RATE
} from './amplitude-session-replay';

export type AmplitudeCompanyTrackingType = 'interactive' | 'mixed' | 'automatic';

export type AmplitudePropertyValue = string | boolean | number;

export function getAmplitudeCompanyTrackingType(
  trackingMode: AuthCompany['companySettings']['trackingMode'] | undefined,
): AmplitudeCompanyTrackingType {
  const mode = trackingMode ?? 'interactive';
  return mode === 'silent' ? 'automatic' : mode;
}

export function hasTrackedTimePast7Days(lastTrackUpdatedAt: string | undefined, now = Date.now()): boolean {
  if (!lastTrackUpdatedAt) {
    return false;
  }

  return now - new Date(lastTrackUpdatedAt).getTime() <= 7 * 24 * 60 * 60 * 1000;
}

export function hasAnyUserTrackedTimePast7Days(users: User[] | undefined, now = Date.now()): boolean {
  return !!users?.some(user => hasTrackedTimePast7Days(user.lastTrack?.updatedAt, now));
}

const AMPLITUDE_IDENTIFY_LOG_DEPLOYMENTS = ['staging'] as const;

export function shouldLogAmplitudeIdentifyPayloads(
  deployment = environment.deployment,
): boolean {
  return (AMPLITUDE_IDENTIFY_LOG_DEPLOYMENTS as readonly string[]).includes(deployment);
}

export interface GuideSegmentValues {
  painpoint: string | undefined;
  companystatus: string | undefined;
  tracked_time_past_7_days: boolean | undefined;
  company_tracking_type: AmplitudeCompanyTrackingType;
  accesslevel: string | undefined;
}

/** Values compared to the Amplitude guide segment. `painpoint` is read from company settings and is not sent by this app. */
export function readGuideSegmentValues(
  company: AuthCompany,
  billingData?: BillingDetailsApiData,
  manageUsers?: User[],
): GuideSegmentValues {
  const trackedTime = company.role === 'owner'
    ? (manageUsers
      ? hasAnyUserTrackedTimePast7Days(manageUsers)
      : hasTrackedTimePast7Days(company.lastTrack?.updatedAt))
    : undefined;

  return {
    painpoint: company.companySettings?.custom?.painpoint,
    companystatus: billingData?.customerStatus,
    tracked_time_past_7_days: trackedTime,
    company_tracking_type: getAmplitudeCompanyTrackingType(company.companySettings?.trackingMode),
    accesslevel: company.role,
  };
}

/** Amplitude user id. Onboarding builds the same `userId|companyId` value. */
export function amplitudeUserId(userId: string, companyId: string): string {
  return userId + '|' + companyId;
}

function getSplitTestValue(
  splitTest: AuthCompany['splitTest'] | undefined,
  name: string,
): string {
  return splitTest?.find(test => test.name === name)?.value ?? '';
}

/** Returns true when company fields that feed Amplitude identify are unchanged. */
export function isAmplitudeCompanyIdentityUnchanged(
  previous: AuthCompany | undefined,
  current: AuthCompany | undefined,
): boolean {
  if (!previous || !current) {
    return previous === current;
  }

  return previous.id === current.id
    && previous.userCount === current.userCount
    && previous.pricingPlan === current.pricingPlan
    && previous.name === current.name
    && previous.role === current.role
    && previous.companyCreatedAt === current.companyCreatedAt
    && previous.subscription?.status === current.subscription?.status
    && previous.companySettings?.trackingMode === current.companySettings?.trackingMode
    && previous.companySettings?.custom?.painpoint === current.companySettings?.custom?.painpoint
    && previous.lastTrack?.updatedAt === current.lastTrack?.updatedAt
    && getSplitTestValue(previous.splitTest, 'popup-signup-trigger') === getSplitTestValue(current.splitTest, 'popup-signup-trigger')
    && getSplitTestValue(previous.splitTest, 'google-auth') === getSplitTestValue(current.splitTest, 'google-auth');
}

interface AmplitudeIdentifyPayloads {
  userId: string;
  userProperties: Record<string, AmplitudePropertyValue>;
  groupType: 'company';
  groupId: string;
  groupProperties: Record<string, AmplitudePropertyValue>;
}

/**
 * GrowthBook feature that acts as the master switch for Amplitude tracking.
 * Defaults to `false`, so deployments without GrowthBook (e.g. private clouds)
 * never initialize Amplitude and produce no console noise. The flag value is
 * resolved by the caller (AppComponent) to avoid a circular DI/import cycle
 * with GrowthBookService -> SegmentService -> AmplitudeService.
 */
export const AMPLITUDE_TRACKING_FLAG = 'amplitude-tracking-flag';

/**
 * GrowthBook feature that loads and shows Amplitude Guides and Surveys.
 * Defaults to `false`, so the engagement bundle stays unloaded until the flag
 * is on. Resolved by the caller (AppComponent) for the same circular-DI
 * reason as `AMPLITUDE_TRACKING_FLAG`.
 */
export const AMPLITUDE_GUIDE_FLAG = 'amplitude-guide-flag';

interface EngagementGuide {
  flagKey?: string;
  lifeCycleState?: {
    isCompleted?: boolean;
  };
}

interface EngagementGuides {
  show(guideKey: string, step?: number): void;
  /** Present after the engagement bundle loads. Returns guides with persisted user state. */
  getAllGuidesAndSurveys?: () => EngagementGuide[];
  /** Guides that are running now: `visible` on screen, or `active` and waiting for their step's page. */
  list?: () => Array<{ key: string, step?: number, status?: 'visible' | 'active' }>;
}

/** Tracked by the engagement SDK each time a guide step is shown. */
const GUIDE_VIEWED_EVENT = '[Guides-Surveys] Guide Viewed';

const GUIDE_PROPERTY = {
  key: '[Guides-Surveys] Key',
  stepIndex: '[Guides-Surveys] Step Index',
  page: '[Guides-Surveys] Page',
} as const;

/** Event the engagement SDK reports to integrations, e.g. a guide shown or dismissed. */
interface EngagementEvent {
  event_type: string;
  event_properties?: Record<string, unknown>;
}

interface EngagementClient {
  gs?: EngagementGuides;
  refresh?: () => Promise<void>;
  setRouter?: (routerFn: (url: string) => void) => void;
  addIntegration?: (integration: { track?: (event: EngagementEvent) => void }) => void;
}

type WindowWithEngagement = Window & {
  engagement?: EngagementClient;
};

function readEngagement(target: Window = window): EngagementClient | undefined {
  return (target as WindowWithEngagement).engagement;
}

@Injectable({
  providedIn: 'root',
})
export class AmplitudeService {
  private static readonly guideReadyPollMs = 200;
  private static readonly guideReadyTimeoutMs = 15000;

  isReady$ = new BehaviorSubject<boolean>(null);
  /** Master switch for Guides and Surveys. Off until GrowthBook says otherwise. */
  private readonly guidesEnabled$ = new BehaviorSubject<boolean>(false);
  /** True once user and company identity were sent, so guide targeting sees them. */
  private readonly identified$ = new BehaviorSubject<boolean>(false);
  /** Amplitude user id sent by the last identify. Tags saved guide progress. */
  private identifiedUserId: string | null = null;
  private initInProgress = false;
  private engagementPluginTask: Promise<void> | null = null;
  constructor(
    private readonly store: Store,
    private readonly router: Router,
    private readonly zone: NgZone,
  ) {}

  /**
   * Reactively enable/disable Amplitude tracking from the GrowthBook
   * `amplitude-tracking-flag`. Designed to be called on every flag emission.
   *
   * The Amplitude SDK cannot be uninstalled once started, so we initialize it
   * lazily on the first enable and then use opt-out to pause/resume tracking
   * (including autocapture). Without an API key — e.g. private clouds — we never
   * initialize and stay completely silent.
   */
  async setTrackingEnabled(enabled: boolean) {
    if (!environment.amplitudeApiKey) {
      this.isReady$.next(false);
      return;
    }

    if (!enabled) {
      if (this.isReady$.value) {
        this.setSdkOptOut(true);
      } else {
        this.isReady$.next(false);
      }
      return;
    }

    if (this.isReady$.value) {
      this.setSdkOptOut(false);
      return;
    }

    if (this.initInProgress) {
      return;
    }
    this.initInProgress = true;
    try {
      await this.startAmplitudeSdk(environment.amplitudeApiKey);
      this.setSdkOptOut(false);
    } finally {
      this.initInProgress = false;
    }
  }

  /**
   * Reactively enable Guides and Surveys from the GrowthBook
   * `amplitude-guide-flag`. The engagement bundle is imported only while the
   * flag is on. Call this before `setTrackingEnabled` so a first-time SDK boot
   * can register the plugin before `amplitude.init`.
   */
  setGuidesEnabled(enabled: boolean) {
    this.guidesEnabled$.next(enabled);
    if (enabled && this.isReady$.value) {
      void this.maybeAddEngagementPlugin();
    }
  }

  /** Reports a caught Amplitude failure without blocking tracking or guides. */
  private reportAmplitudeError(message: string, error: unknown): void {
    Sentry.captureException(error, { extra: { message } });
  }

  /** Wraps amplitude.setOptOut so the reactive toggle can be stubbed in tests. */
  private setSdkOptOut(optOut: boolean) {
    amplitude.setOptOut(optOut);
  }

  /** Boots the Amplitude SDK. Isolated from the gating logic so it can be stubbed in tests. */
  private async startAmplitudeSdk(apiKey: string) {
    await this.maybeAddSessionReplayPlugin();
    await this.maybeAddEngagementPlugin();

    try {
      await this.initAmplitudeClient(apiKey).promise;
      this.isReady$.next(true);
    } catch (error) {
      this.isReady$.next(false);
      this.reportAmplitudeError('Error initializing Amplitude', error);
    }
  }

  /** Wraps amplitude.init so boot order can be asserted without stubbing the SDK module. */
  private initAmplitudeClient(apiKey: string) {
    return amplitude.init(apiKey, {
      autocapture: {
        pageViews: true,
        sessions: true,
        formInteractions: true,
        fileDownloads: false,
        elementInteractions: true,
      },
    });
  }

  /** Wraps amplitude.add so plugin registration can be asserted in tests. */
  private addSdkPlugin(plugin: Parameters<typeof amplitude.add>[0]) {
    amplitude.add(plugin);
  }

  /**
   * Loads Session Replay only for capable devices. Failures here must not block
   * Amplitude event tracking.
   */
  private async maybeAddSessionReplayPlugin(): Promise<void> {
    const capability = evaluateSessionReplayCapability(readSessionReplayDeviceSignals());
    if (!capability.allowed) {
      return;
    }

    try {
      const { sessionReplayPlugin } = await import(
        /* webpackChunkName: "amplitude-session-replay" */
        '@amplitude/plugin-session-replay-browser'
      );
      amplitude.add(sessionReplayPlugin({
        sampleRate: SESSION_REPLAY_SAMPLE_RATE,
        debugMode: !environment.production,
        forceSessionTracking: true,
        performanceConfig: { enabled: true },
      }));
    } catch (error) {
      this.reportAmplitudeError('Error loading Amplitude session replay', error);
    }
  }

  /**
   * Registers Guides and Surveys before Amplitude init so the plugin shares
   * the analytics API key and user identity. Skipped entirely while the guide
   * flag is off, so the engagement chunk is never downloaded. A load failure
   * must not block event tracking.
   */
  private maybeAddEngagementPlugin(): Promise<void> {
    if (!this.guidesEnabled$.value) {
      return Promise.resolve();
    }

    if (!this.engagementPluginTask) {
      this.engagementPluginTask = this.loadEngagementPlugin();
    }

    return this.engagementPluginTask;
  }

  private async loadEngagementPlugin(): Promise<void> {
    try {
      const { plugin: engagementPlugin } = await import(
        /* webpackChunkName: "amplitude-engagement" */
        '@amplitude/engagement-browser'
      );
      if (!this.guidesEnabled$.value) {
        this.engagementPluginTask = null;
        return;
      }
      this.addSdkPlugin(engagementPlugin());
      this.registerGuideRouter();
    } catch (error) {
      this.engagementPluginTask = null;
      this.reportAmplitudeError('Error loading Amplitude engagement', error);
    }
  }

  identify(
    isReady: boolean,
    user: AuthUser,
    company: AuthCompany,
    billingData?: BillingDetailsApiData,
    manageUsers?: User[],
    hasBillingAccess = false,
  ) {
    if (!company?.id || !user?.id) {
      return;
    }

    const payloads = this.buildIdentifyPayloads(user, company, billingData, manageUsers, hasBillingAccess);

    if (!environment.production) {
      console.log('[Amplitude guide segment]', readGuideSegmentValues(company, billingData, manageUsers));
    }

    if (isReady) {
      this.sendIdentifyPayloads(payloads);
      this.identifiedUserId = payloads.userId;
      this.identified$.next(true);
      return;
    }

    this.logIdentifyPayloads(payloads);
  }

  private buildIdentifyPayloads(
    user: AuthUser,
    company: AuthCompany,
    billingData?: BillingDetailsApiData,
    manageUsers?: User[],
    hasBillingAccess = false,
  ): AmplitudeIdentifyPayloads {
    const billingCycle = billingData?.plan?.split(' ')[1] || '';
    const pricingPlan = billingData?.plan?.split(' ')[0] || '';
    const homeCta = company.splitTest?.find(test => test.name === 'popup-signup-trigger')?.value ?? '';
    const googleAuth = company.splitTest?.find(test => test.name === 'google-auth')?.value ?? '';
    const userProperties: Record<string, AmplitudePropertyValue> = {
      email: user.email,
      name: user.name,
      companyname: company.name,
      accesslevel: company.role,
      hasBillingAccess,
      homecta: homeCta,
      tdcompanyid: company.id,
      tduserid: user.id,
    };
    if (billingData) {
      userProperties.companystatus = billingData.customerStatus;
      userProperties.pricingplan = pricingPlan.toLocaleLowerCase();
      userProperties.billingcycle = billingCycle.toLocaleLowerCase();
    }
    if (company.role === 'owner' && googleAuth) {
      userProperties.googleauth = googleAuth;
    }

    const companyTrackingType = getAmplitudeCompanyTrackingType(company.companySettings?.trackingMode);
    const groupProperties: Record<string, AmplitudePropertyValue> = {
      name: company.name,
      plan: company.pricingPlan,
      totalseats: company.userCount,
      createdate: company.companyCreatedAt,
      status: company.subscription.status,
      istrackingtime: this.store.selectSnapshot(AuthSelectors.isTrackingTime),
      users_count: company.userCount,
      company_tracking_type: companyTrackingType,
    };
    // Onboarding also sends this; resending it here keeps guide targeting from depending on that one call.
    const painpoint = company.companySettings?.custom?.painpoint;
    if (painpoint) {
      groupProperties.painpoint = painpoint;
    }
    if (company.role === 'owner') {
      groupProperties.tracked_time_past_7_days = manageUsers
        ? hasAnyUserTrackedTimePast7Days(manageUsers)
        : hasTrackedTimePast7Days(company.lastTrack?.updatedAt);
    }
    if (billingData) {
      groupProperties.trialstartdate = billingData.trialStartDate || '';
      groupProperties.trialenddate = billingData.trialEndDate || '';
    }

    return {
      userId: amplitudeUserId(user.id, company.id),
      userProperties,
      groupType: 'company',
      groupId: company.id,
      groupProperties,
    };
  }

  private sendIdentifyPayloads(payloads: AmplitudeIdentifyPayloads) {
    amplitude.setUserId(payloads.userId);
    amplitude.identify(this.toIdentify(payloads.userProperties));
    amplitude.setGroup(payloads.groupType, payloads.groupId);
    amplitude.groupIdentify(payloads.groupType, payloads.groupId, this.toIdentify(payloads.groupProperties));
  }

  private logIdentifyPayloads(payloads: AmplitudeIdentifyPayloads) {
    if (!shouldLogAmplitudeIdentifyPayloads()) {
      return;
    }

    console.log('[Amplitude] identify (SDK not ready)', {
      userId: payloads.userId,
      properties: payloads.userProperties,
    });
    console.log('[Amplitude] groupIdentify (SDK not ready)', {
      groupType: payloads.groupType,
      groupId: payloads.groupId,
      properties: payloads.groupProperties,
    });
  }

  private toIdentify(properties: Record<string, AmplitudePropertyValue>) {
    const identify = new amplitude.Identify();
    for (const [key, value] of Object.entries(properties)) {
      identify.set(key, value);
    }
    return identify;
  }

  reset() {
    if (!this.isReady$.value) {
      return;
    }

    amplitude.reset();
    this.identifiedUserId = null;
    this.identified$.next(false);
  }

  trackEvent(eventName: string, eventData?: any) {
    if (!this.isReady$.value) {
      return;
    }

    amplitude.track(eventName, eventData);
  }

  /**
   * Waits until the GrowthBook guide flag is on, then until user and company
   * identity were sent, so the refresh below targets the real user.
   * Analytics can be ready before Guides has fetched nudges, so refresh first.
   * Amplitude audience targeting decides which guide to show, so this does not
   * call `gs.show`. Clears saved progress when this user already completed the
   * guide, and leaves a guide alone while it is on screen.
   */
  showGuideWhenReady(guideKey: string, step?: number) {
    this.debugGuides('waiting to show', {
      guideKey,
      step,
      guidesEnabled: this.guidesEnabled$.value,
      identified: this.identified$.value,
    });
    return this.guidesEnabled$.pipe(
      filter(enabled => enabled),
      take(1),
      switchMap(() => this.identified$.pipe(
        filter(identified => identified),
        take(1),
      )),
      switchMap(() => this.waitForEngagement()),
      switchMap(engagement => from(this.refreshThenShow(engagement, guideKey, step))),
    );
  }

  /** Polls until the engagement plugin has attached `gs.show`. */
  private waitForEngagement() {
    return timer(0, AmplitudeService.guideReadyPollMs).pipe(
      map(() => readEngagement()),
      filter((engagement): engagement is EngagementClient => !!engagement?.gs?.show),
      take(1),
      timeout(AmplitudeService.guideReadyTimeoutMs),
      catchError(error => {
        this.reportAmplitudeError('Amplitude guides did not load in time', error);
        return of(undefined);
      }),
    );
  }

  /**
   * Multi-page guides change pages through this router. Without it the SDK
   * reloads the whole app, which restarts Amplitude and loses the next step.
   */
  private registerGuideRouter(): void {
    this.isReady$.pipe(
      filter(ready => ready === true),
      take(1),
      switchMap(() => this.waitForEngagement()),
    ).subscribe(engagement => {
      this.debugGuides('router registration', {
        hasEngagement: !!engagement,
        hasSetRouter: typeof engagement?.setRouter === 'function',
        hasAddIntegration: typeof engagement?.addIntegration === 'function',
      });
      engagement?.setRouter?.(url => this.navigateFromGuide(url));
      engagement?.addIntegration?.({ track: event => this.onGuideEvent(event) });
    });
  }

  /** Receives every event the engagement SDK tracks. */
  private onGuideEvent(event: EngagementEvent): void {
    this.debugGuides(`event ${event.event_type}`, event.event_properties);
    if (event.event_type === GUIDE_VIEWED_EVENT) {
      this.saveViewedStep(event.event_properties ?? {});
    }
  }

  /** Remembers the step on screen and its page so later visits know where the guide left off. */
  private saveViewedStep(properties: Record<string, unknown>): void {
    const guideKey = properties[GUIDE_PROPERTY.key];
    const step = properties[GUIDE_PROPERTY.stepIndex];
    const path = (properties[GUIDE_PROPERTY.page] as { path?: unknown } | undefined)?.path;
    if (typeof guideKey !== 'string' || typeof step !== 'number' || typeof path !== 'string' || !this.identifiedUserId) {
      return;
    }

    this.store.dispatch(new SaveGuideProgress({
      guideKey,
      step,
      path: routePath(path),
      ownerId: this.identifiedUserId,
      savedAt: Date.now(),
    }));
  }

  /** Guide diagnostics, only locally and on staging. */
  private debugGuides(message: string, details?: unknown): void {
    if (!environment.production || shouldLogAmplitudeIdentifyPayloads()) {
      console.log(`[Amplitude guides] ${message}`, details);
    }
  }

  private navigateFromGuide(url: string): void {
    this.debugGuides('router called', { url });
    const target = new URL(url, window.location.origin);
    if (target.origin !== window.location.origin) {
      this.assignLocation(target.href);
      return;
    }

    this.zone.run(() => {
      void this.router.navigateByUrl(`${target.pathname}${target.search}${target.hash}`);
    });
  }

  /** Wraps window.location.assign so cross-origin guide links can be asserted in tests. */
  private assignLocation(href: string): void {
    window.location.assign(href);
  }

  private async refreshThenShow(engagement: EngagementClient | undefined, guideKey: string, step?: number) {
    this.debugGuides('show requested', { guideKey, step, hasEngagement: !!engagement?.gs?.show });
    if (!this.guidesEnabled$.value || !engagement?.gs?.show) {
      return;
    }

    try {
      await engagement.refresh?.();
    } catch (error) {
      this.reportAmplitudeError('Error refreshing Amplitude guides', error);
    }

    if (!this.guidesEnabled$.value) {
      return;
    }

    if (this.hasCompletedGuide(engagement, guideKey)) {
      this.debugGuides('show skipped: completed', { guideKey });
      this.clearGuideProgress(guideKey);
      return;
    }

    if (this.isGuideRunning(engagement, guideKey)) {
      this.debugGuides('show skipped: already running', { guideKey });
      return;
    }
  }

  private clearGuideProgress(guideKey: string): void {
    if (this.store.selectSnapshot(GuideProgressState.progress)?.guideKey === guideKey) {
      this.store.dispatch(new ClearGuideProgress());
    }
  }

  /**
   * A visible guide is already on screen. Audience targeting decides what to
   * show, so this path never calls `gs.show` to restart or reopen it.
   */
  private isGuideRunning(engagement: EngagementClient, guideKey: string): boolean {
    try {
      const guides = engagement.gs?.list?.() ?? [];
      this.debugGuides('running guides', guides);
      return guides.some(guide => guide.key === guideKey && guide.status === 'visible');
    } catch (error) {
      this.reportAmplitudeError('Error reading active Amplitude guides', error);
      return false;
    }
  }

  /**
   * Reads persisted guide state after refresh. A completed guide drops its
   * saved progress. Audience targeting decides whether it is shown again.
   */
  private hasCompletedGuide(engagement: EngagementClient, guideKey: string): boolean {
    try {
      const guides = engagement.gs?.getAllGuidesAndSurveys?.() ?? [];
      return guides.some(guide => guide.flagKey === guideKey && guide.lifeCycleState?.isCompleted === true);
    } catch (error) {
      this.reportAmplitudeError('Error reading Amplitude guide completion', error);
      return false;
    }
  }
}
